// Kirim dokumen tahap 0, on stage 0 (Rencana Kerja & Anggaran PBJ) of a PBJ
// project (API GET/POST /pbj/projects/:id/handovers, migration 0037). In
// PA/KPA mode a button opens a dialog: the PPK's email, an optional note,
// and the saved stage-0 documents that will go along (the newest saved
// version of each activity, read from ../project/ActivityDocs). The PPK
// then opens the project as a member (Anggota project, ./Members.tsx). Under
// it the history of what was sent, for every mode.
import { useEffect, useId, useState } from "react";
import * as API from "../api";
import type { PbjActivity, PbjHandover } from "../api";
import { formatDateTime } from "../lib/format";
import { useActivityDocs } from "../project/ActivityDocs";
import { Dialog, Field, Notice } from "../ui/components";
import { Glyph } from "../ui/icons";

export const HANDOVER_TITLE = "Kirim dokumen Rencana Kerja & Anggaran";
export const NO_STAGE_DOCS = "Simpan minimal satu dokumen tahap Rencana Kerja & Anggaran dulu.";

/** The person's words for a handover error, by the API's code. */
export function handoverError(err: unknown): string {
  const code = err instanceof API.ApiError ? err.body?.code : undefined;
  if (code === "handover_pa_kpa_only") return "Dokumen tahap Rencana Kerja & Anggaran dikirim oleh PA/KPA. Ganti mode peran ke PA/KPA.";
  if (code === "recipient_not_found") return "Akun PPK dengan email itu tidak ditemukan.";
  if (code === "no_stage_documents") return NO_STAGE_DOCS;
  if (code === "recipient_is_self") return "Pilih akun PPK lain, bukan akun Anda sendiri.";
  return API.errorMessage(err);
}

interface Props {
  projectId: string;
  /** Stage 0's activities (those with an output can carry a saved document). */
  activities: PbjActivity[];
  /** The current mode may send (PA/KPA). */
  canSend: boolean;
}

export function HandoverSection({ projectId, activities, canSend }: Props) {
  const docs = useActivityDocs();
  const [history, setHistory] = useState<PbjHandover[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    API.listPbjHandovers(projectId).then(
      (h) => live && setHistory(h),
      (err) => live && setHistoryError(API.errorMessage(err)),
    );
    return () => {
      live = false;
    };
  }, [projectId]);

  // The newest saved version of each stage-0 activity, in the order of work.
  const saved = activities.flatMap((a) => {
    const s = a.output ? docs?.state(a.id)?.saved : undefined;
    return s ? [{ key: a.id, title: a.output, version: s.version }] : [];
  });

  return (
    <section className="pbj-handover" aria-labelledby="pbj-handover-title">
      <div className="pbj-handover-head">
        <div className="pbj-docs-head">
          <h3 id="pbj-handover-title" className="pbj-subtitle">
            {HANDOVER_TITLE}
          </h3>
          <p className="pbj-docs-lead">
            {canSend
              ? "Kirim dokumen tahap Rencana Kerja & Anggaran yang sudah disimpan ke PPK. PPK menerima akses ke project ini dan melanjutkan dari tahap berikutnya."
              : "PA/KPA mengirim dokumen tahap Rencana Kerja & Anggaran ke PPK. Ganti mode ke PA/KPA untuk mengirim."}
          </p>
        </div>
        {canSend ? (
          <button type="button" className="btn btn-secondary btn-sm" aria-haspopup="dialog" onClick={() => setOpen(true)}>
            <Glyph name="send" size={16} />
            <span>{HANDOVER_TITLE}</span>
          </button>
        ) : null}
      </div>
      {sent ? (
        <p className="pbj-next" role="status">
          {sent}
        </p>
      ) : null}
      {historyError ? (
        <p className="pj-empty">Riwayat pengiriman belum dapat dimuat. {historyError}</p>
      ) : history && history.length > 0 ? (
        <ul className="rows" aria-label="Riwayat kirim dokumen tahap Rencana Kerja & Anggaran">
          {history.map((h) => (
            <li key={h.id} className="row">
              <span className="row-main">
                <span className="row-title">
                  {h.from.name} ke {h.to.name} ({h.to.email})
                </span>
                <span className="row-meta">
                  {h.documents.length} dokumen: {h.documents.map((d) => `${d.title} versi ${d.version}`).join("; ")}
                </span>
                {h.note ? <span className="row-meta">Catatan: {h.note}</span> : null}
              </span>
              <span className="row-side row-meta num">{formatDateTime(h.createdAt)}</span>
            </li>
          ))}
        </ul>
      ) : history ? (
        <p className="pj-empty">Belum ada dokumen tahap Rencana Kerja & Anggaran yang dikirim.</p>
      ) : null}
      <HandoverDialog
        projectId={projectId}
        open={open}
        saved={saved}
        onClose={() => setOpen(false)}
        onSent={(h) => {
          setOpen(false);
          setHistory((list) => [h, ...(list ?? [])]);
          setSent(`${h.documents.length} dokumen tahap Rencana Kerja & Anggaran terkirim ke ${h.to.name}.`);
        }}
      />
    </section>
  );
}

function HandoverDialog({
  projectId,
  open,
  saved,
  onClose,
  onSent,
}: {
  projectId: string;
  open: boolean;
  saved: { key: string; title: string; version: number | null }[];
  onClose: () => void;
  onSent: (h: PbjHandover) => void;
}) {
  const id = useId();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setNote("");
    setError(null);
  }, [open]);

  const clean = email.trim();
  const send = async () => {
    if (!clean || busy) return;
    setBusy(true);
    setError(null);
    try {
      onSent(await API.sendPbjHandover(projectId, { email: clean, ...(note.trim() ? { note: note.trim() } : {}) }));
    } catch (err) {
      setError(handoverError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={HANDOVER_TITLE}
      description="Dokumen dikirim ke akun PPK di Pluto, bukan lewat email."
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button type="button" className="btn btn-primary" disabled={!clean || saved.length === 0 || busy} aria-busy={busy || undefined} onClick={() => void send()}>
            <Glyph name="send" size={18} />
            <span>{busy ? "Mengirim" : "Kirim"}</span>
          </button>
        </>
      }
    >
      <form
        className="pbj-handover-form"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <Field id={`${id}-email`} label="Email akun PPK" required hint="Email yang dipakai PPK untuk masuk ke Pluto.">
          <input
            id={`${id}-email`}
            type="email"
            data-autofocus=""
            autoComplete="off"
            maxLength={320}
            value={email}
            aria-describedby={`${id}-email-hint`}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </Field>
        <Field id={`${id}-note`} label="Catatan (opsional)">
          <textarea id={`${id}-note`} rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        </Field>
      </form>
      <p className="pbj-subtitle">Dokumen yang dikirim</p>
      {saved.length === 0 ? (
        <Notice tone="warning" title="Belum ada dokumen tersimpan">
          <p>{NO_STAGE_DOCS}</p>
        </Notice>
      ) : (
        <ul className="ad-skip-list" aria-label="Dokumen tahap Rencana Kerja & Anggaran yang dikirim">
          {saved.map((d) => (
            <li key={d.key}>
              <span>
                {d.title}, versi {d.version}
              </span>
            </li>
          ))}
        </ul>
      )}
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </Dialog>
  );
}
